import type { StaffRouter } from '@eklevo/api/staff-trpc'
import { checkBrandContrast, DEFAULT_BRAND, normalizeHexColor } from '@eklevo/shared/brand'
import {
  COMPANY_LEGAL_FIELDS,
  COMPANY_LEGAL_LIMITS,
  normalizeTaxId,
  type CompanyLegalField,
} from '@eklevo/shared/company'
import type { TimeZone } from '@eklevo/shared/domain'
import { isGenericMailbox } from '@eklevo/shared/email'
import { LOCALES, type BackOfficeMessageKey, type Locale } from '@eklevo/shared/i18n/backoffice'
import {
  findNoticePlaceholders,
  noticePlaceholderCode,
  placeholdersOfCode,
} from '@eklevo/shared/notice-model'
import { characterCount, isSafeLine, isSafeMultilineText } from '@eklevo/shared/text'
import type { inferRouterInputs } from '@trpc/server'

/*
 * The company wizard's draft (ADR 0009 §7), which lives only in the open tab: never sent until
 * «Crear empresa», never kept in the browser's storage. Its steps, the checks the page makes
 * before going on (the same rules as the server's, packages/core/src/services/company.ts, which
 * answers each problem at its field's path anyway), the payload of `companies.create`, and where
 * each problem the server answers belongs: its step and its field.
 */

export const WIZARD_STEPS = ['company', 'people', 'hr', 'notice', 'courses', 'summary'] as const
export type WizardStep = (typeof WIZARD_STEPS)[number]

/** The server's limits (company.ts and NOTICE_BODY, packages/core), as the page counts them. */
export const WIZARD_LIMITS = {
  name: { min: 2, max: 120 },
  personName: 100,
  email: 254,
  url: 2000,
  notice: { min: 20, max: 20_000 },
  hrTeam: 10,
  courses: 50,
  legalName: COMPANY_LEGAL_LIMITS.legalName,
  address: COMPANY_LEGAL_LIMITS.address,
  privacyContact: COMPANY_LEGAL_LIMITS.privacyContact,
} as const

/**
 * The lengths of the payload's bounds (companyDraft): the most a field takes in, so that the
 * server answers a code for a long value instead of refusing the whole request.
 */
export const INPUT_BOUNDS = { line: 1000, color: 20, url: 4000, body: 25_000 } as const

export interface HrPersonDraft {
  /** Stable while the person is on the list, so that an error stays with its row. */
  key: number
  firstName: string
  lastName: string
  email: string
}

export type NoticeKind = 'url' | 'text'

export interface NoticeDraft {
  /** For a language other than the company's: whether it has a notice too. */
  enabled: boolean
  kind: NoticeKind
  url: string
  body: string
}

/**
 * The logo (ADR 0007; ADR 0009 §6): uploaded from the browser as soon as it is chosen, and kept by
 * its upload's token once the malware scan found it clean; the server checks it again and stores
 * it with the company on «Crear empresa». Its file's name is the page's only.
 */
export type LogoDraft =
  { status: 'uploading'; file: string } | { status: 'ready'; file: string; token: string }

/**
 * A company's legal data as a form holds it (decided on 2026-10-18), each as typed: blank for none.
 * The wizard's first step and «Editar» share it.
 */
export type LegalDraft = Record<CompanyLegalField, string>

export interface WizardDraft extends LegalDraft {
  name: string
  defaultLocale: Locale
  timeZone: TimeZone
  brand: { enabled: boolean; primary: string; accent: string }
  logo: LogoDraft | null
  hrTeam: HrPersonDraft[]
  notice: Record<Locale, NoticeDraft>
  /** The ids of the catalogue's courses chosen. */
  courses: string[]
}

export type CompanyPayload = inferRouterInputs<StaffRouter>['companies']['create']

/** The code of each field's problem, by field (see `fieldStep`); never the value. */
export type FieldErrors = Readonly<Record<string, string>>

const emptyNotice = (): NoticeDraft => ({ enabled: false, kind: 'url', url: '', body: '' })

export function newDraft(): WizardDraft {
  return {
    name: '',
    legalName: '',
    taxId: '',
    address: '',
    privacyContact: '',
    defaultLocale: 'es',
    timeZone: 'Europe/Madrid',
    brand: { enabled: false, primary: DEFAULT_BRAND.primary, accent: DEFAULT_BRAND.accent },
    logo: null,
    hrTeam: [newHrPerson(1)],
    notice: { es: emptyNotice(), ca: emptyNotice(), en: emptyNotice() },
    courses: [],
  }
}

export function newHrPerson(key: number): HrPersonDraft {
  return { key, firstName: '', lastName: '', email: '' }
}

/** The languages of the notice, the company's first: it always has one there. */
export function noticeLocales(draft: WizardDraft): Locale[] {
  return [
    draft.defaultLocale,
    ...LOCALES.filter((l) => l !== draft.defaultLocale && draft.notice[l].enabled),
  ]
}

/** What `companies.create` receives: the draft as typed, which the server trims and checks. */
export function toPayload(draft: WizardDraft): CompanyPayload {
  return {
    company: {
      name: draft.name,
      ...legalPayload(draft),
      defaultLocale: draft.defaultLocale,
      timeZone: draft.timeZone,
      brand: draft.brand.enabled
        ? {
            primary: normalizeHexColor(draft.brand.primary) ?? draft.brand.primary,
            accent: normalizeHexColor(draft.brand.accent) ?? draft.brand.accent,
          }
        : null,
      logo: draft.logo?.status === 'ready' ? { upload: draft.logo.token } : null,
    },
    hrTeam: draft.hrTeam.map(({ firstName, lastName, email }) => ({ firstName, lastName, email })),
    notice: noticeLocales(draft).map((locale) => {
      const n = draft.notice[locale]
      return {
        locale,
        url: n.kind === 'url' ? n.url : null,
        body: n.kind === 'text' ? n.body : null,
      }
    }),
    courses: draft.courses,
  }
}

/** The legal data as the server receives it: each as typed, which it trims (blank: none). */
export function legalPayload(draft: LegalDraft): LegalDraft {
  return {
    legalName: draft.legalName,
    taxId: draft.taxId,
    address: draft.address,
    privacyContact: draft.privacyContact,
  }
}

/**
 * The legal data as the server would store them (each trimmed, the tax id as normalizeTaxId
 * leaves it, the contact in lower case; blank: none), to tell whether a form changes them.
 */
export function legalValues(draft: LegalDraft): Record<CompanyLegalField, string | null> {
  const taxId = draft.taxId.trim()
  return {
    legalName: draft.legalName.trim() || null,
    taxId: taxId ? (normalizeTaxId(taxId) ?? taxId) : null,
    address: draft.address.trim() || null,
    privacyContact: draft.privacyContact.trim().toLowerCase() || null,
  }
}

/** The problems of the legal data the page finds, by field (the server's rules; all optional). */
export function legalProblems(draft: LegalDraft): Record<string, string> {
  const problems: Record<string, string> = {}
  const at = (field: CompanyLegalField, code: string | null) => {
    if (code) problems[field] = code
  }
  const legalName = draft.legalName.trim()
  if (legalName)
    at(
      'legalName',
      lineProblem(legalName, WIZARD_LIMITS.legalName.max, WIZARD_LIMITS.legalName.min),
    )
  const taxId = draft.taxId.trim()
  if (taxId && !normalizeTaxId(taxId)) at('taxId', 'invalid')
  const address = draft.address.trim()
  if (address) at('address', lineProblem(address, WIZARD_LIMITS.address))
  const contact = draft.privacyContact.trim()
  if (contact && (contact.length > WIZARD_LIMITS.privacyContact || !EMAIL.test(contact)))
    at('privacyContact', 'invalid')
  return problems
}

const LOGO_ERRORS = [
  'uploading',
  'scanning',
  'too-large',
  'empty',
  'wrong-type',
  'wrong-dimensions',
  'infected',
  'scan-failed',
  'missing',
  'expired',
  'network',
  'scan-timeout',
] as const

/** Why the logo is refused, or did not finish uploading (UPLOAD_REFUSALS and the page's own). */
export function logoError(code: string): BackOfficeMessageKey {
  return (LOGO_ERRORS as readonly string[]).includes(code)
    ? `wizard.error.logo.${code as (typeof LOGO_ERRORS)[number]}`
    : 'wizard.error.logo.failed'
}

/** The step a field belongs to. */
export function fieldStep(field: string): number {
  const root = field.split('.')[0]!
  const step: WizardStep =
    root === 'hrTeam'
      ? 'hr'
      : root === 'notice'
        ? 'notice'
        : root === 'courses'
          ? 'courses'
          : 'company'
  return WIZARD_STEPS.indexOf(step)
}

/** A line a person typed: required, between `min` and `max` characters, nothing invisible. */
export function lineProblem(value: string, max: number, min = 1): string | null {
  const count = characterCount(value)
  if (count === 0) return 'required'
  if (count < min) return 'too-short'
  if (count > max) return 'too-long'
  if (!isSafeLine(value)) return 'invalid-characters'
  return null
}

// An address as zod's email() takes it (the server's check): close enough for the page, which
// shows the server's answer at the field anyway.
const EMAIL =
  /^(?!\.)(?!.*\.\.)[A-Za-z0-9_'+\-.]*[A-Za-z0-9_+-]@(?:[A-Za-z0-9][A-Za-z0-9-]*\.)+[A-Za-z]{2,}$/

export function isHttpsUrl(value: string): boolean {
  try {
    const url = new URL(value)
    return url.protocol === 'https:' && url.hostname !== '' && !url.username && !url.password
  } catch {
    return false
  }
}

/** The contrast rules a pair of colours fails, by the colour each one is about. */
export function contrastProblems(primary: string, accent: string): Record<string, string> {
  const problems: Record<string, string> = {}
  for (const c of checkBrandContrast({ primary, accent }))
    if (!c.passed) {
      const field = c.rule.startsWith('accent') ? 'brand.accent' : 'brand.primary'
      problems[field] ??= `contrast-${c.rule}`
    }
  return problems
}

/**
 * The problems of one step, as the page finds them before going on (the server checks it all
 * again on «Crear empresa»). The steps of the people and the summary have none.
 */
export function checkStep(draft: WizardDraft, step: number): Record<string, string> {
  const errors: Record<string, string> = {}
  const at = (field: string, code: string | null) => {
    if (code) errors[field] = code
  }
  switch (WIZARD_STEPS[step]) {
    case 'company': {
      at('name', lineProblem(draft.name.trim(), WIZARD_LIMITS.name.max, WIZARD_LIMITS.name.min))
      Object.assign(errors, legalProblems(draft))
      // A logo still going: the company would be created without it.
      if (draft.logo?.status === 'uploading') at('logo', 'uploading')
      if (draft.brand.enabled) {
        const primary = normalizeHexColor(draft.brand.primary)
        const accent = normalizeHexColor(draft.brand.accent)
        if (!primary) at('brand.primary', 'invalid')
        if (!accent) at('brand.accent', 'invalid')
        if (primary && accent) Object.assign(errors, contrastProblems(primary, accent))
      }
      break
    }
    case 'hr': {
      if (draft.hrTeam.length === 0) at('hrTeam', 'required')
      if (draft.hrTeam.length > WIZARD_LIMITS.hrTeam) at('hrTeam', 'too-many')
      const seen = new Set<string>()
      for (const p of draft.hrTeam) {
        const field = `hrTeam.${p.key}`
        at(`${field}.firstName`, lineProblem(p.firstName.trim(), WIZARD_LIMITS.personName))
        at(`${field}.lastName`, lineProblem(p.lastName.trim(), WIZARD_LIMITS.personName))
        const email = p.email.trim().toLowerCase()
        at(
          `${field}.email`,
          email === ''
            ? 'required'
            : email.length > WIZARD_LIMITS.email || !EMAIL.test(email)
              ? 'invalid'
              : isGenericMailbox(email)
                ? 'generic-mailbox'
                : seen.has(email)
                  ? 'duplicate'
                  : null,
        )
        seen.add(email)
      }
      break
    }
    case 'notice': {
      for (const locale of noticeLocales(draft)) {
        const n = draft.notice[locale]
        if (n.kind === 'url') {
          const url = n.url.trim()
          at(
            `notice.${locale}.url`,
            url === ''
              ? 'required'
              : url.length > WIZARD_LIMITS.url || !isHttpsUrl(url)
                ? 'invalid'
                : null,
          )
        } else {
          const body = n.body.trim()
          const count = characterCount(body)
          const placeholders = findNoticePlaceholders(body)
          at(
            `notice.${locale}.body`,
            count === 0
              ? 'required'
              : count < WIZARD_LIMITS.notice.min
                ? 'too-short'
                : count > WIZARD_LIMITS.notice.max
                  ? 'too-long'
                  : !isSafeMultilineText(body)
                    ? 'invalid-characters'
                    : placeholders.length
                      ? noticePlaceholderCode(placeholders)
                      : null,
          )
        }
      }
      break
    }
    case 'courses':
      if (draft.courses.length > WIZARD_LIMITS.courses) at('courses', 'too-many')
      break
    default:
      break
  }
  return errors
}

/**
 * The problems of the fields the page shows: those of a notice in a language the company no
 * longer has (or of the format it does not use), of a person removed, of colours not used or of
 * a course not chosen are kept aside, as their fields are, and never stop the way on.
 */
export function shownErrors(draft: WizardDraft, errors: FieldErrors): Record<string, string> {
  const locales = new Set(noticeLocales(draft))
  const people = new Set(draft.hrTeam.map((p) => String(p.key)))
  const courses = new Set(draft.courses)
  const shown: Record<string, string> = {}
  for (const [field, code] of Object.entries(errors)) {
    const [root, second, third] = field.split('.')
    if (root === 'notice' && second !== undefined) {
      const locale = second as Locale
      if (!locales.has(locale)) continue
      if (third !== (draft.notice[locale].kind === 'url' ? 'url' : 'body')) continue
    }
    if (root === 'hrTeam' && second !== undefined && !people.has(second)) continue
    if (root === 'brand' && !draft.brand.enabled) continue
    if (root === 'courses' && second !== undefined && !courses.has(second)) continue
    shown[field] = code
  }
  return shown
}

/**
 * Going on to step `target`: each step before it is checked again, its earlier findings replaced
 * by what the page finds now, and the problems the server answered stay until their field
 * changes. The page's problems after it, and the first step with a problem shown, if any.
 */
export function checkBefore(
  draft: WizardDraft,
  target: number,
  found: { page: FieldErrors; server: FieldErrors },
): { page: Record<string, string>; stop: number | null } {
  let page: Record<string, string> = { ...found.page }
  for (let i = 0; i < target; i++) {
    page = {
      ...Object.fromEntries(Object.entries(page).filter(([field]) => fieldStep(field) !== i)),
      ...checkStep(draft, i),
    }
    const shown = shownErrors(draft, { ...page, ...found.server })
    if (Object.keys(shown).some((field) => fieldStep(field) === i)) return { page, stop: i }
  }
  return { page, stop: null }
}

/** The fields of the company's data that a problem names by their own name. */
const COMPANY_FIELDS: ReadonlySet<string> = new Set([
  'name',
  'defaultLocale',
  'timeZone',
  ...COMPANY_LEGAL_FIELDS,
])

/**
 * The field of a problem the server answered (its path in the payload), in the draft that was
 * sent: a person by their row, a notice by its language, a course by its id. Null for a path the
 * page does not know.
 */
export function issueField(sent: WizardDraft, path: readonly (string | number)[]): string | null {
  const [root, second, third] = path
  if (root === 'company') {
    if (typeof second === 'string' && COMPANY_FIELDS.has(second))
      return path.length === 2 ? second : null
    if (second === 'brand' && (third === 'primary' || third === 'accent')) return `brand.${third}`
    if (second === 'logo') return 'logo'
    return null
  }
  if (root === 'hrTeam') {
    if (second === undefined) return 'hrTeam'
    const person = typeof second === 'number' ? sent.hrTeam[second] : undefined
    if (!person) return 'hrTeam'
    if (third === 'firstName' || third === 'lastName' || third === 'email')
      return `hrTeam.${person.key}.${third}`
    return 'hrTeam'
  }
  if (root === 'notice') {
    if (second === undefined) return 'notice'
    const locale = typeof second === 'number' ? noticeLocales(sent)[second] : undefined
    if (!locale) return 'notice'
    if (third === 'url' || third === 'body') return `notice.${locale}.${third}`
    // The notice of a language as a whole: an address or a text, whichever it has.
    return `notice.${locale}.${sent.notice[locale].kind === 'url' ? 'url' : 'body'}`
  }
  if (root === 'courses') {
    if (second === undefined) return 'courses'
    const id = typeof second === 'number' ? sent.courses[second] : undefined
    return id ? `courses.${id}` : 'courses'
  }
  return null
}

/** The server's problems, by field; those of a path the page does not know, apart. */
export function issuesToErrors(
  sent: WizardDraft,
  issues: readonly { path: (string | number)[]; code: string }[],
): { errors: Record<string, string>; unknown: number } {
  const errors: Record<string, string> = {}
  let unknown = 0
  for (const issue of issues) {
    const field = issueField(sent, issue.path)
    if (field) errors[field] ??= issue.code
    else unknown++
  }
  return { errors, unknown }
}

/** What a field's message names: of a notice with blanks of the model, which (the code says). */
export function errorValues(code: string): { placeholders: string } | undefined {
  const placeholders = placeholdersOfCode(code)
  return placeholders ? { placeholders } : undefined
}

/** What a field's problem says, in the page's language. */
export function errorMessage(field: string, code: string): BackOfficeMessageKey {
  if (code === 'required') {
    if (field === 'hrTeam') return 'wizard.error.hr.required'
    if (field === 'notice') return 'wizard.error.notice.missing'
    return 'wizard.error.required'
  }
  if (field === 'name') {
    if (code === 'too-short') return 'wizard.error.name.too-short'
    if (code === 'too-long') return 'wizard.error.name.too-long'
    if (code === 'taken') return 'wizard.error.name.taken'
    if (code === 'invalid-characters') return 'wizard.error.line.invalid-characters'
  }
  if (field === 'defaultLocale' && code === 'notice-missing') return 'company.edit.noticeMissing'
  if (field === 'defaultLocale' || field === 'timeZone') return 'wizard.error.choice'
  if (field === 'legalName') {
    if (code === 'too-short') return 'wizard.error.name.too-short'
    if (code === 'too-long') return 'wizard.error.legalName.too-long'
    if (code === 'invalid-characters') return 'wizard.error.line.invalid-characters'
  }
  if (field === 'address') {
    if (code === 'too-long') return 'wizard.error.address.too-long'
    if (code === 'invalid-characters') return 'wizard.error.line.invalid-characters'
  }
  if (field === 'taxId') {
    if (code === 'invalid') return 'wizard.error.taxId.invalid'
    if (code === 'taken') return 'wizard.error.taxId.taken'
  }
  if (field === 'privacyContact' && code === 'invalid') return 'wizard.error.email.invalid'
  if (field === 'logo') return logoError(code)
  if (field.startsWith('brand.')) {
    if (code === 'invalid') return 'wizard.error.color.invalid'
    if (code.startsWith('contrast-')) return 'wizard.error.color.contrast'
  }
  if (field.startsWith('hrTeam.')) {
    if (field.endsWith('.email')) {
      if (code === 'invalid') return 'wizard.error.email.invalid'
      if (code === 'generic-mailbox') return 'wizard.error.email.generic-mailbox'
      if (code === 'duplicate') return 'wizard.error.email.duplicate'
    } else {
      if (code === 'too-long') return 'wizard.error.person.too-long'
      if (code === 'invalid-characters') return 'wizard.error.line.invalid-characters'
    }
  }
  if (field === 'hrTeam' && code === 'too-many') return 'wizard.error.hr.too-many'
  if (field === 'notice' && code === 'default-locale-missing') return 'wizard.error.notice.missing'
  if (field.startsWith('notice.')) {
    if (code === 'invalid') return 'wizard.error.notice.url'
    if (code === 'too-short') return 'wizard.error.notice.too-short'
    if (code === 'too-long') return 'wizard.error.notice.too-long'
    if (code === 'invalid-characters') return 'wizard.error.notice.invalid-characters'
    if (code === 'url-or-text') return 'wizard.error.required'
    if (placeholdersOfCode(code)) return 'wizard.error.notice.placeholder'
  }
  if (field === 'courses' && code === 'too-many') return 'wizard.error.courses.too-many'
  if (field.startsWith('courses.') && code === 'not-available')
    return 'wizard.error.course.not-available'
  return 'wizard.error.field'
}
